import { apiSlice } from "../../app/api/apiSlice";
import { urlConfig } from "../../config";

// Types
export interface GiftcardCountry {
  isoName: string;
  name: string;
  flagUrl: string;
}

export interface GiftcardBrand {
  brandId: number;
  brandName: string;
  logoUrl: string;
  productCount: number;
}

export interface GiftcardProduct {
  productId: number;
  productName: string;
  denominationType: "FIXED" | "RANGE";

  // Sender amounts (what user pays - typically USD)
  minSenderAmount: number | null;
  maxSenderAmount: number | null;
  fixedSenderAmounts: number[];
  senderCurrencyCode: string;
  senderFee: number;

  // Recipient amounts (gift card face value - local currency)
  minRecipientAmount: number | null;
  maxRecipientAmount: number | null;
  fixedRecipientAmounts: number[];
  recipientCurrencyCode: string;

  // Mapping for FIXED denominations: recipient amount -> sender amount
  recipientToSenderMap: Record<string, number>;

  logoUrls: string[];
  redeemInstructions: string;
}

export interface GiftcardServiceStatus {
  isEnabled: boolean;
  supportMessage?: string;
  maxQuantity: number;
}

export interface GiftcardPreviewRequest {
  productId: number;
  countryCode: string; // Required for cached product lookup
  amountUSD?: number; // Sender amount (for FIXED products)
  recipientAmount?: number; // Recipient/face value (for RANGE products)
  quantity?: number;
}

export interface GiftcardPreviewResponse {
  productId: number;
  productName: string;
  brandId: number;
  brandName: string;
  countryIso: string;
  countryName: string;
  amountUSD: number;
  amountNGN: number;
  reloadlyFeeNGN: number;
  gcbuyingFeeNGN: number;
  totalNGN: number;
  quantity: number;
  exchangeRateUsed: number;
  walletBalance: number;
  canPurchase: boolean;
  redeemInstructions: string;
}

export interface GiftcardConfirmRequest {
  productId: number;
  countryCode: string; // Required for cached product lookup
  amountUSD?: number; // Sender amount (for FIXED products)
  recipientAmount?: number; // Recipient/face value (for RANGE products)
  quantity?: number;
  recipientEmail?: string;
  recipientPhone?: string;
}

export interface GiftcardConfirmResponse {
  request_id: string;
  status: string;
  message: string;
  totalNGN: number;
  newWalletBalance: number;
}

export interface GiftcardCode {
  cardNumber: string;
  pinCode?: string;
  redemptionUrl?: string;
}

export interface GiftcardTransaction {
  request_id: string;
  productName: string;
  brandName: string;
  countryName: string;
  amountUSD: number;
  totalNGN: number;
  quantity: number;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface GiftcardTransactionsResponse {
  transactions: GiftcardTransaction[];
  total: number;
  page: number;
  limit: number;
}

export interface GiftcardTransactionDetails {
  request_id: string;
  productId: number;
  productName: string;
  brandId: number;
  brandName: string;
  countryIso: string;
  countryName: string;
  amountUSD: number; // Sender amount (legacy field name)
  senderCurrencyCode: string;
  recipientAmount: number; // Gift card face value
  recipientCurrencyCode: string;
  amountNGN: number;
  reloadlyFeeNGN: number;
  gcbuyingFeeNGN: number;
  totalNGN: number;
  quantity: number;
  exchangeRateUsed: number;
  status: string;
  giftcardCodes?: GiftcardCode[];
  redeemInstructions?: string;
  failureReason?: string;
  refunded?: boolean;
  recipientEmail?: string;
  reloadlyTransactionId?: string;
  created_at: string;
  updated_at: string;
}

export const giftcardsSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    // Get service status
    getGiftcardStatus: build.query<GiftcardServiceStatus, void>({
      query: () => ({
        url: `${urlConfig.reloadlyUrl}/reloadly/giftcards/status`,
        method: "GET",
      }),
    }),

    // Get countries
    getGiftcardCountries: build.query<GiftcardCountry[], void>({
      query: () => ({
        url: `${urlConfig.reloadlyUrl}/reloadly/giftcards/countries`,
        method: "GET",
      }),
    }),

    // Get brands by country
    getGiftcardBrands: build.query<GiftcardBrand[], string>({
      query: (countryCode) => ({
        url: `${urlConfig.reloadlyUrl}/reloadly/giftcards/brands/${countryCode}`,
        method: "GET",
      }),
    }),

    // Get products by brand
    getGiftcardProducts: build.query<
      GiftcardProduct[],
      { countryCode: string; brandId: number }
    >({
      query: ({ countryCode, brandId }) => ({
        url: `${urlConfig.reloadlyUrl}/reloadly/giftcards/products/${countryCode}/${brandId}`,
        method: "GET",
      }),
    }),

    // Search products
    searchGiftcardProducts: build.query<
      GiftcardProduct[],
      { countryCode: string; searchTerm: string }
    >({
      query: ({ countryCode, searchTerm }) => ({
        url: `${urlConfig.reloadlyUrl}/reloadly/giftcards/search/${countryCode}?q=${encodeURIComponent(searchTerm)}`,
        method: "GET",
      }),
    }),

    // Preview purchase
    previewGiftcard: build.mutation<
      GiftcardPreviewResponse,
      GiftcardPreviewRequest
    >({
      query: (body) => ({
        url: `${urlConfig.reloadlyUrl}/reloadly/giftcards/preview`,
        method: "POST",
        body,
      }),
    }),

    // Confirm purchase
    confirmGiftcard: build.mutation<
      GiftcardConfirmResponse,
      GiftcardConfirmRequest
    >({
      query: (body) => ({
        url: `${urlConfig.reloadlyUrl}/reloadly/giftcards/confirm`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Wallet", "Giftcards"],
    }),

    // Confirm purchase with OTP (after 401 challenge)
    confirmGiftcardWithOtp: build.mutation<
      GiftcardConfirmResponse,
      { body: GiftcardConfirmRequest; challengeId: string; otpCode: string }
    >({
      query: ({ body, challengeId, otpCode }) => ({
        url: `${urlConfig.reloadlyUrl}/reloadly/giftcards/confirm`,
        method: "POST",
        body,
        headers: {
          "x-otp-challenge": challengeId,
          "x-otp-code": otpCode,
        },
      }),
      invalidatesTags: ["Wallet", "Giftcards"],
    }),

    // Get transactions
    getGiftcardTransactions: build.query<
      GiftcardTransactionsResponse,
      { page?: number; limit?: number }
    >({
      query: ({ page = 1, limit = 10 }) => ({
        url: `${urlConfig.reloadlyUrl}/reloadly/giftcards/transactions?page=${page}&limit=${limit}`,
        method: "GET",
      }),
      providesTags: ["Giftcards"],
    }),

    // Get transaction details
    getGiftcardTransactionDetails: build.query<
      GiftcardTransactionDetails,
      string
    >({
      query: (requestId) => ({
        url: `${urlConfig.reloadlyUrl}/reloadly/giftcards/transactions/${requestId}`,
        method: "GET",
      }),
      providesTags: (result, error, requestId) => [
        { type: "Giftcards", id: requestId },
      ],
    }),

    // Retry failed transaction
    retryGiftcardTransaction: build.mutation<GiftcardConfirmResponse, string>({
      query: (requestId) => ({
        url: `${urlConfig.reloadlyUrl}/reloadly/giftcards/transactions/${requestId}/retry`,
        method: "POST",
      }),
      invalidatesTags: ["Wallet", "Giftcards"],
    }),

    // Retry failed transaction with OTP (after 401 challenge)
    retryGiftcardTransactionWithOtp: build.mutation<
      GiftcardConfirmResponse,
      { requestId: string; challengeId: string; otpCode: string }
    >({
      query: ({ requestId, challengeId, otpCode }) => ({
        url: `${urlConfig.reloadlyUrl}/reloadly/giftcards/transactions/${requestId}/retry`,
        method: "POST",
        headers: {
          "x-otp-challenge": challengeId,
          "x-otp-code": otpCode,
        },
      }),
      invalidatesTags: ["Wallet", "Giftcards"],
    }),
  }),
});

export const {
  useGetGiftcardStatusQuery,
  useGetGiftcardCountriesQuery,
  useGetGiftcardBrandsQuery,
  useGetGiftcardProductsQuery,
  useSearchGiftcardProductsQuery,
  usePreviewGiftcardMutation,
  useConfirmGiftcardMutation,
  useConfirmGiftcardWithOtpMutation,
  useGetGiftcardTransactionsQuery,
  useGetGiftcardTransactionDetailsQuery,
  useRetryGiftcardTransactionMutation,
  useRetryGiftcardTransactionWithOtpMutation,
} = giftcardsSlice;
